import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import IconButton from "src/components/IconButton";
import Spinner from "src/sudokit/components/Spinner";
import { EMAIL_PREFERENCE_OPTIONS } from "../../../../src/shared/constants";
import { areEmailPreferencesEqual, getDefaultEmailPreferences, normalizeEmailPreferences } from "../../../../src/shared/emailPreferences";
import Auth from "../../Auth";
import { SudoButton } from "../../sudokit/components/SudoButton";
import { SudoTooltip } from "../../sudokit/components/SudoTooltip";

type CustomerIoActionsProps = {
  userId?: string;
  setToast: (content: string) => void;
};

export function CustomerIoActions({ userId, setToast }: CustomerIoActionsProps) {
  const [unsuppressing, setUnsuppressing] = useState(false);
  const [emailPreferences, setEmailPreferences] = useState<Record<string, boolean>>(getDefaultEmailPreferences);
  const [emailPreferencesDraft, setEmailPreferencesDraft] = useState<Record<string, boolean>>(getDefaultEmailPreferences);
  const [emailPreferencesDirty, setEmailPreferencesDirty] = useState(false);
  const [savingEmailPreferences, setSavingEmailPreferences] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [loadingPreferences, setLoadingPreferences] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const emailButtonRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  const loadPreferences = useCallback(async () => {
    if (!userId) {
      setEmailPreferences(getDefaultEmailPreferences());
      setEmailPreferencesDraft(getDefaultEmailPreferences());
      setEmailPreferencesDirty(false);
      setLoadError(null);
      setLoadingPreferences(false);
      return;
    }

    try {
      setLoadingPreferences(true);
      setLoadError(null);
      const response = await Auth.fetchWithIdToken(`/api/admin/user/${userId}/email-preferences/fetch`, {});
      const normalized = normalizeEmailPreferences(response?.preferences || {});
      setEmailPreferences(normalized);
      setEmailPreferencesDraft(normalized);
      setEmailPreferencesDirty(false);
    } catch (error) {
      const message = (error as Error)?.message || "Failed to load Customer.io preferences";
      setLoadError(message);
      setToast(message);
    } finally {
      setLoadingPreferences(false);
    }
  }, [setToast, userId]);

  useEffect(() => {
    const defaults = getDefaultEmailPreferences();
    setEmailPreferences(defaults);
    setEmailPreferencesDraft(defaults);
    setEmailPreferencesDirty(false);
    setPopoverOpen(false);
    setLoadError(null);
    setLoadingPreferences(false);
  }, [userId]);

  useEffect(() => {
    if (!popoverOpen) {
      return;
    }

    const handleClick = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node) && emailButtonRef.current && !emailButtonRef.current.contains(event.target as Node)) {
        setPopoverOpen(false);
        setEmailPreferencesDraft(emailPreferences);
        setEmailPreferencesDirty(false);
      }
    };

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setPopoverOpen(false);
        setEmailPreferencesDraft(emailPreferences);
        setEmailPreferencesDirty(false);
      }
    };

    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);

    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [emailPreferences, popoverOpen]);

  const handleUnsuppress = async () => {
    if (!userId) return;

    setUnsuppressing(true);
    try {
      await Auth.fetchWithIdToken(`/api/admin/user/${userId}/unsuppress`, {
        method: "POST",
      });
      setToast("Customer.io user unsuppressed");
    } catch (error: any) {
      setToast(`Failed to unsuppress user: ${error.message}`);
    } finally {
      setUnsuppressing(false);
    }
  };

  const handleEmailPreferenceChange = (key: string, value: boolean) => {
    setEmailPreferencesDraft((prev) => {
      const next = { ...prev, [key]: value };
      setEmailPreferencesDirty(!areEmailPreferencesEqual(next, emailPreferences));
      return next;
    });
  };

  const handleSetAll = (value: boolean) => {
    setEmailPreferencesDraft((prev) => {
      const next = Object.fromEntries(Object.keys(prev).map((key) => [key, value])) as Record<string, boolean>;
      setEmailPreferencesDirty(!areEmailPreferencesEqual(next, emailPreferences));
      return next;
    });
  };

  const handleSavePreferences = async () => {
    if (!userId || !emailPreferencesDirty) {
      setPopoverOpen(false);
      return;
    }

    setSavingEmailPreferences(true);
    try {
      await Auth.fetchWithIdToken(`/api/admin/user/${userId}/email-preferences`, {
        preferences: emailPreferencesDraft,
      });
      setEmailPreferences(emailPreferencesDraft);
      setEmailPreferencesDirty(false);
      setPopoverOpen(false);
      setToast("Email preferences updated");
      await loadPreferences();
    } catch (error: any) {
      setToast(`Failed to update email preferences: ${error.message}`);
    } finally {
      setSavingEmailPreferences(false);
    }
  };

  const popoverTrigger = useMemo(() => {
    if (!userId) {
      return (
        <SudoTooltip text="Select a user to manage Customer.io preferences.">
          <SudoButton
            ref={emailButtonRef}
            variant="secondary"
            size="default"
            disabled
          >
            Manage email preferences
          </SudoButton>
        </SudoTooltip>
      );
    }

    return (
      <SudoTooltip text="Adjust which Customer.io campaigns this user receives.">
        <SudoButton
          ref={emailButtonRef}
          variant="secondary"
          size="default"
          onClick={(e) => {
            e.preventDefault();
            if (popoverOpen) {
              setPopoverOpen(false);
              setEmailPreferencesDraft(emailPreferences);
              setEmailPreferencesDirty(false);
            } else {
              loadPreferences();
              setPopoverOpen(true);
            }
          }}
        >
          Manage email preferences
        </SudoButton>
      </SudoTooltip>
    );
  }, [emailPreferences, loadPreferences, popoverOpen, userId]);

  return (
    <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
      <h4 className="text-base font-semibold text-purple-900 mb-2">Customer.io Messaging</h4>
      <p className="text-sm text-purple-700 mb-4">Toggle campaign-level preferences or unblock messaging for this user.</p>
      <div className="flex flex-wrap gap-6">
        <SudoTooltip text="Unsuppress the user in Customer.io and sync their attributes so they can receive lifecycle messaging again.">
          <SudoButton
            onClick={(e) => {
              e.preventDefault();
              handleUnsuppress();
            }}
            disabled={!userId || unsuppressing}
            variant="secondary"
            size="default"
            state={unsuppressing ? "loading" : "default"}
          >
            Unsuppress Customer.io
          </SudoButton>
        </SudoTooltip>

        <div className="relative">
          {popoverTrigger}
          {popoverOpen && (
            <div
              ref={popoverRef}
              className="absolute right-0 z-50 mt-2 rounded-xl border border-gray-200 bg-white p-4 text-left shadow-2xl w-[28rem]"
            >
              <div className="mb-3 flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-gray-900">Email Preferences</p>
                  <p className="text-xs text-gray-500">Choose which campaigns this user should receive.</p>
                </div>

                <IconButton
                  src="/assets/close.svg"
                  onClick={() => {
                    setPopoverOpen(false);
                    setEmailPreferencesDraft(emailPreferences);
                    setEmailPreferencesDirty(false);
                  }}
                  iconSize={20}
                />
              </div>

              <div className="mb-3 flex flex-wrap gap-2">
                <SudoButton
                  variant="tertiaryGrey"
                  size="xsmall"
                  disabled={loadingPreferences}
                  onClick={(e) => {
                    e.preventDefault();
                    handleSetAll(true);
                  }}
                >
                  Subscribe to all
                </SudoButton>
                <SudoButton
                  variant="tertiaryGrey"
                  size="xsmall"
                  disabled={loadingPreferences}
                  onClick={(e) => {
                    e.preventDefault();
                    handleSetAll(false);
                  }}
                >
                  Unsubscribe from all
                </SudoButton>
              </div>

              {loadingPreferences ? (
                <div className="flex flex-row items-center justify-center py-10">
                  {" "}
                  <Spinner />
                </div>
              ) : (
                <div className="space-y-3">
                  {EMAIL_PREFERENCE_OPTIONS.map((option) => (
                    <label
                      key={option.key}
                      className="flex items-start gap-3 text-sm text-gray-800"
                    >
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4 cursor-pointer rounded border-gray-300 text-app-primary-cta focus:ring-app-primary-cta"
                        checked={!!emailPreferencesDraft[option.key]}
                        disabled={loadingPreferences}
                        onChange={(event) => handleEmailPreferenceChange(option.key, event.target.checked)}
                      />
                      <span>
                        <span className="font-medium">{option.label}</span>
                        <span className="mt-0.5 block text-xs text-gray-500">{option.description}</span>
                      </span>
                    </label>
                  ))}
                </div>
              )}

              {loadError ? <p className="mt-2 text-xs text-red-600">{loadError}</p> : null}

              <div className="mt-4 flex items-center justify-between gap-2">
                <span className={`text-xs ${emailPreferencesDirty ? "text-red-600" : "text-gray-400"}`}>{emailPreferencesDirty ? "You have unsaved changes" : " "}</span>
                <div className="flex gap-2">
                  <SudoButton
                    variant="tertiaryGrey"
                    size="small"
                    disabled={loadingPreferences}
                    onClick={() => {
                      setPopoverOpen(false);
                      setEmailPreferencesDraft(emailPreferences);
                      setEmailPreferencesDirty(false);
                    }}
                  >
                    Cancel
                  </SudoButton>
                  <SudoButton
                    variant="primary"
                    size="small"
                    disabled={!emailPreferencesDirty || savingEmailPreferences || loadingPreferences}
                    state={savingEmailPreferences ? "loading" : "default"}
                    onClick={(e) => {
                      e.preventDefault();
                      handleSavePreferences();
                    }}
                  >
                    Save Preferences
                  </SudoButton>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
